/**
 * Pricing page CMS copy.
 *
 * Source of truth: `/app/memory/PooPluckers_Pricing_Copy.md`.
 *
 * Hard rules baked into this file:
 *   - NO hardcoded plan prices, discount %s, or commission %s. The
 *     Pricing page fetches `/api/public/customer-plans` and
 *     `/api/public/partner-tiers` and binds those rows into the
 *     plan/tier cards at runtime.
 *   - The only numbers that DO live in copy are the per-dog base
 *     price, the recurring-visit discount %, and the securement fee —
 *     and those are pulled from the live `/api/config/pricing`
 *     endpoint via `useConfig().pricing`.
 *   - Brand caps: `Pluck`, `Pluckers`, `Plucking`, `PooPartner`,
 *     `PooPluckers` are ALWAYS capitalised. Pre-commit grep
 *     (`[a-z]pluck`, `[a-z]lucker`) must return zero hits here.
 *   - Honesty constraint: no background-check / insurance claims.
 *     Verification language is Stripe-anchored ("Stripe identity
 *     verification" — same standard countless marketplaces rely on).
 *   - Member-discount honesty (concern-based, not date-based): the
 *     pricing engine's commission-cap clamp reduces the effective
 *     member discount below the tier maximum on small bookings —
 *     the partner's take is invariant and the platform can't absorb
 *     more than its own commission. Any customer-facing rate MUST
 *     therefore be derived per-booking from the engine's `/quote`
 *     response, and never rendered as a flat tier-max literal. The
 *     legacy Free/Plus/Premium plan-card grid was retired from the
 *     public page because it displayed hardcoded tier-max rates
 *     that the clamp could silently erode. The
 *     `test_pricing_config_integrity.py::test_family_g_savings_chip_no_percent_leak`
 *     guard in the backend meta suite enforces the derivation rule
 *     on the SavingsPreviewChip source.
 */
export const PRICING_HERO = {
  eyebrow: "Pricing",
  // Item 14 refinement: PRICING_HERO was migrated from the
  // legacy `headline_primary` + full-second-half `headline_accent`
  // split (which pre-dated the phrase-accent norm — the "accent" was
  // literally the second half of the sentence, not a phrase within it)
  // to the canonical `headline_primary` (FULL sentence) +
  // `headline_accent` (PHRASE within it) shape. `<AccentHeadline>`
  // finds "your pack" inside "Pricing tailored to your pack." and
  // wraps it in the shared brand token. The trailing period stays
  // slate — matches the phrase-accent norm used by every other
  // section H2 on this page.
  headline_primary: "Pricing tailored to your pack.",
  headline_accent: "your pack",
  intro:
    "Tell us about your pack and see your exact price in two taps.",
};

export const PRICING_BASE = {
  kick: "How a Plucking is priced",
  headline: "First-dog base + a small add-on for each extra pup.",
  // Item 14 accent phrase for the section H2. Substring MUST appear
  // verbatim in `headline`. See `lib/AccentHeadline.jsx`.
  headline_accent: "each extra pup",
  body:
    "Every Plucking starts with a base price for your first dog. Each additional dog in the pack adds a small flat amount \u2014 no linear \u201cper dog per visit\u201d multipliers. Bookings on PooPluckers use the platform price so you\u2019ll see the exact quote before you confirm.",
  // The dollar / percent values for these cards bind to
  // `useConfig().pricing` at render time. Copy below describes the
  // mechanic; the JSX layer formats the live numbers.
  //
  // Item N: only ONE card in this section now \u2014 "Per-dog pricing"
  // (base + additional). The old "Recurring discount 0% off" and
  // "Unsecured yards +$0" cards were removed because zero-value
  // cards read as broken. When either config value becomes non-zero
  // in the future, add its dedicated card back \u2014 not before.
  per_dog_card: {
    label: "Per-dog pricing",
    first_dog_prefix: "First dog from",
    additional_prefix: "Each additional dog",
    body:
      "Two numbers: a base for the first dog and a small flat add-on for every additional pup. You\u2019ll see the exact quote \u2014 every dog, every fee, the total \u2014 before you confirm.",
  },
  reassurance:
    "You\u2019ll always get a full quote \u2014 every dog, every fee, the total \u2014 before you pay a cent.",
};

// ---------------------------------------------------------------------------
// PRICING_PLANS — RETIRED (see file docstring — commission-cap clamp
// concern). The Free/Plus/Premium customer-plan card grid was retired
// from `/pricing` because it displayed hardcoded tier-max discount
// rates that the engine's commission-cap clamp can silently erode on
// small bookings. Any live member-savings surface must render a
// PER-BOOKING DERIVED percent via `SavingsPreviewChip` (which reads
// deltas from `/quote`), never a hardcoded literal like "10% off" or
// "17% off".
//
// DO NOT re-introduce a `PRICING_PLANS` export here with hardcoded
// rates. The `test_pricing_config_integrity.py::test_family_g_savings_chip_no_percent_leak`
// guard trips if a future dispatch re-adds hardcoded rate literals
// to the customer-facing chip source.
// ---------------------------------------------------------------------------

/* ---------------------------------------------------------------------------
   PRICING_QUOTE — copy for the Build-Your-Pack interactive quote tool that
   replaced the static Free/Plus/Premium membership card grid. Two-step
   funnel (pack size + frequency) wired to /api/quote — see QuoteTool.jsx.
   --------------------------------------------------------------------------- */
export const PRICING_QUOTE = {
  kick: "Build your pack",
  headline: "See your exact per-visit price.",
  // Item 14 accent phrase for the Build-Your-Pack H2. Highlights
  // the value-carrying noun-phrase (the price surface itself).
  headline_accent: "exact per-visit price",
  body:
    "Two taps. Tell us your pack size and how often you want a Plucking, and we\u2019ll quote your real per-visit price from the live engine \u2014 every dog, every fee, the total.",
};

// ---------------------------------------------------------------------------
// PRICING_TIERS — RETIRED (see file docstring + PRD §Pricing
// Config Change Protocol §Structural guard).
//
// The old "For PooPartners" copy block on the pricing page exposed the
// commission mechanic ("PooPluckers takes a small commission…", "the more
// you grow with us, the lower your commission") AND surfaced an
// interpolated worked example ("at Standard (20%), PooPluckers' fee is
// $16") that named the commission percentage. Both were retired in
// favor of `<PartnerTiersPanel showPayoutTable={false} />`, which renders
// rate-free tier cards (badge + description + CTA) from the live
// `/api/public/partner-tiers` payload. The payout table with actual
// numbers only renders when `showPayoutTable={true}` — that path is
// authenticated (partner onboarding), not public marketing.
//
// DO NOT re-introduce a `PRICING_TIERS` export here. If a future
// dispatch needs partner-facing copy, add fields to `PartnerTiersPanel`
// (auth-gated) or to a new component that is NEVER wired into the
// public /pricing route. The `Pricing.rateExposure.copyAudit.test.jsx`
// suite has a source-file grep that FAILS if `PRICING_TIERS` is
// re-exported from this file.
// ---------------------------------------------------------------------------

export const PRICING_TRUST = {
  kick: "Trust & safety",
  headline: "Real local pros. Stripe-secured money.",
  // Item 14 — accent phrase for the Trust & safety H2. Highlights
  // the Stripe payment-security half of the pair (the differentiator).
  headline_accent: "Stripe-secured money",
  body:
    "We\u2019re shipping trust the right way \u2014 start with what\u2019s actually live, layer the rest only when it\u2019s real.",
  items: [
    {
      key: "stripe-identity",
      icon: "ShieldCheck",
      title: "Stripe identity verification",
      body:
        "Every PooPartner will complete Stripe identity verification before they can accept a Plucking. Same standard countless marketplaces rely on.",
      note: "At launch",
    },
    {
      key: "stripe-escrow",
      icon: "Lock",
      title: "Stripe payouts",
      body:
        "Funds are paid out after a successful Plucking, and PooPartners cash out through Stripe Connect.",
      note: "At launch",
    },
    {
      key: "quote-first",
      icon: "FileText",
      title: "Quote before you pay",
      body:
        "Every dog, every fee, the total \u2014 surfaced up front. You only pay after the Plucking is done.",
      note: "Live",
    },
  ],
};

export const PRICING_FAQ = [
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel a plan or stop bookings from your dashboard whenever you like.",
  },
  {
    q: "What if my PooPartner can\u2019t make it?",
    a: "We\u2019ll rematch you with another PooPartner nearby. If we can\u2019t deliver the Plucking, you don\u2019t pay.",
  },
  {
    q: "Can I set up a recurring Plucking?",
    a: "Yes. Pick weekly or every-other-week when you book, and we'll keep a PooPartner on your yard on a consistent cadence. Change or pause the schedule anytime from your dashboard.",
  },
  {
    q: "When do PooPartners get paid?",
    a: "Funds are paid out after a successful Plucking, and payouts run through Stripe Connect on the PooPartner\u2019s first available Stripe payout date.",
  },
];

export const PRICING_CTA = {
  headline: "Pick the deal that fits your week.",
  sub:
    "We're launching neighborhood by neighborhood \u2014 join the waitlist " +
    "to be first in line when booking opens in your area, or join the " +
    "network and start earning where you already work.",
  customer_label: "Join the waitlist",
  customer_to: "/#waitlist",
  partner_label: "Become a PooPartner",
  partner_to: "/signup?role=provider",
};

export const PRICING_ERROR_BANNER = {
  // Pricing pages must render an explicit failure surface when the
  // public plan/tier endpoints fail — silent fallbacks would risk
  // showing stale or fabricated numbers.
  title: "Pricing temporarily unavailable.",
  body: "Try again shortly.",
  retry_label: "Retry",
};
